import { ErrorBoundary } from "@sentry/react";
import { Suspense } from "react";
import { Form } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod/v4";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useForm } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { PasswordSchema } from "../common/validations";
import { Button } from "../components/Button";
import { useBranding } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { AuthPage } from "../layout/AuthLayout";
import { InvitationCard } from "./InvitationCard";
import { useAuth, useInvitation } from "./useAuth";

const Schema = z.object({
	email: z
		.string({
			error: "Email field is required.",
		})
		.email("Email is not valid."),
	password: PasswordSchema,
	firstName: z.string({
		error: "First Name is required.",
	}),
	lastName: z.string({
		error: "Last Name is required.",
	}),
	accept_terms: z.boolean().refine((value) => value, {
		message: "You must accept the terms and conditions",
	}),
});

export const PageSignUp = () => {
	const { register } = useAuth();
	const branding = useBranding();
	const [searchParams] = useSearchParams();
	const inviteToken = searchParams.get("inviteToken") ?? undefined;
	const { isError: isInvitationError, isLoading: isInvitationLoading } =
		useInvitation(inviteToken);

	const form = useForm({
		validationSchema: toFormikValidationSchema(Schema),
		initialValues: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			subscribed: false,
			accept_terms: false,
		},
		onSubmit: (values) =>
			register.mutateAsync({
				...values,
				inviteToken,
			}),
	});

	return (
		<AuthPage title="Sign up">
			{inviteToken && (
				<ErrorBoundary
					fallback={({ error }) => <InvitationCard.Error error={error} />}
				>
					<Suspense fallback={<InvitationCard.Suspense />}>
						<InvitationCard
							token={inviteToken}
							cta="Please sign up below to join"
						/>
					</Suspense>
				</ErrorBoundary>
			)}
			<Form className="auth-form" onSubmit={form.handleSubmit}>
				<h1 className="my-4">{`Sign up to ${branding.appName}`}</h1>

				<div className="full-name">
					<ExtraForm.Group className="text-start" label="First Name">
						<ExtraForm.Control
							placeholder="John"
							type="text"
							{...form.getFieldProps("firstName")}
							{...form.getFieldState("firstName")}
						/>
					</ExtraForm.Group>

					<ExtraForm.Group className="text-start" label="Last Name">
						<ExtraForm.Control
							placeholder="Doe"
							type="text"
							{...form.getFieldProps("lastName")}
							{...form.getFieldState("lastName")}
						/>
					</ExtraForm.Group>
				</div>

				<ExtraForm.Group label="Email">
					<ExtraForm.Control
						placeholder="john.doe@example.com"
						type="email"
						{...form.getFieldProps("email")}
						{...form.getFieldState("email")}
					/>
				</ExtraForm.Group>

				<ExtraForm.Group label="Password">
					<ExtraForm.Control
						placeholder="6+ characters"
						type="password"
						{...form.getFieldProps("password")}
						{...form.getFieldState("password")}
					/>
				</ExtraForm.Group>

				<Form.Group className="mb-1">
					<Form.Check
						type="checkbox"
						label="Subscribe to our weekly newsletter"
						id="signup-subscribe-switch"
						{...form.getFieldProps("subscribed")}
						{...form.getFieldState("subscribed")}
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Check
						type="checkbox"
						label={
							<>
								I agree to{" "}
								<a
									target="_blank"
									href="https://firestartermarketing.ai/terms-of-service"
									rel="noreferrer"
								>
									Terms and Conditions
								</a>
							</>
						}
						id="accept-terms"
						{...form.getFieldProps("accept_terms")}
						{...form.getFieldState("accept_terms")}
					/>

					{form.touched.accept_terms && (
						<div className="text-danger">{form.errors.accept_terms}</div>
					)}
				</Form.Group>

				<Button
					full
					className="my-3"
					disabled={isInvitationLoading || isInvitationError}
					loading={form.isSubmitting}
					type="submit"
				>
					Create account
				</Button>

				<div className="auth-form-actions">
					Already have an account?{" "}
					<Link to={LinkGen.Login(inviteToken)}>Sign In</Link>
				</div>
				<footer>
					{branding.footerText ??
						`© ${new Date().getFullYear()} ${branding.appName}. All rights reserved`}
				</footer>
			</Form>
		</AuthPage>
	);
};
